import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import managedAxios, { isCancelError } from "@/utils/managedAxios";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import { 
  Loader2, Lock, CheckCircle2, Clock, Target, Brain, 
  TrendingUp, AlertTriangle, FileText, ArrowRight, ArrowLeft,
  Play, Trophy, Shield, Zap, Timer, BarChart3
} from "lucide-react";

const PrelimsTestSeriesPageNew = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [suiteData, setSuiteData] = useState(null);
  const [activeTest, setActiveTest] = useState(null);
  const [testInProgress, setTestInProgress] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [testStartTime, setTestStartTime] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [testResults, setTestResults] = useState(null);

  // Auth guard - redirect if logged out during test
  useEffect(() => {
    if (!user || !token) {
      navigate('/auth', { replace: true });
    }
  }, [user, token, navigate]);

  const isPro = user?.role === "PRO";

  // Test type icons and colors
  const testTypeConfig = {
    foundation: { 
      icon: Shield, 
      color: "from-blue-500 to-cyan-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    pyq_dominant: { 
      icon: Target, 
      color: "from-purple-500 to-indigo-500",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    },
    trap_heavy: { 
      icon: AlertTriangle, 
      color: "from-orange-500 to-red-500",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200"
    },
    full_length: { 
      icon: Timer, 
      color: "from-emerald-500 to-teal-500",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200"
    },
    last_30_day: { 
      icon: Brain, 
      color: "from-pink-500 to-rose-500",
      bgColor: "bg-pink-50",
      borderColor: "border-pink-200"
    }
  };

  // Fetch test suite overview
  const fetchSuiteOverview = useCallback(async () => {
    try {
      const { data } = await managedAxios.get(`${API_BASE}/prelims-test-series/suite-overview`);
      setSuiteData(data);
    } catch (err) {
      toast({ variant: "destructive", description: "Failed to load test series" });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSuiteOverview();
  }, [fetchSuiteOverview]);

  // Timer effect
  useEffect(() => {
    let interval;
    if (testInProgress && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleSubmitTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [testInProgress, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTest = async (testType, testNumber = 1) => {
    setLoading(true);
    try {
      const { data } = await managedAxios.post(`${API_BASE}/prelims-test-series/start-test`, {
        test_type: testType,
        test_number: testNumber
      });
      
      setActiveTest(data);
      setTestInProgress(true);
      setCurrentQuestionIndex(0);
      setAnswers([]);
      setTimeRemaining(data.duration_minutes * 60);
      setTestStartTime(Date.now());
      setShowResults(false);
      
      toast({ description: `Starting ${data.title}...` });
    } catch (err) {
      toast({ 
        variant: "destructive", 
        description: err.response?.data?.detail || "Failed to start test" 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (option) => {
    const newAnswers = [...answers];
    const existingIndex = newAnswers.findIndex(a => a.question_index === currentQuestionIndex);
    
    const answerData = {
      question_index: currentQuestionIndex,
      selected_option: option,
      time_spent_seconds: Math.floor((Date.now() - testStartTime) / 1000)
    };
    
    if (existingIndex >= 0) {
      newAnswers[existingIndex] = answerData;
    } else {
      newAnswers.push(answerData);
    }
    
    setAnswers(newAnswers);
  };

  const handleSkipQuestion = () => {
    const newAnswers = [...answers];
    const existingIndex = newAnswers.findIndex(a => a.question_index === currentQuestionIndex);
    
    const answerData = {
      question_index: currentQuestionIndex,
      selected_option: null, // null = skipped
      time_spent_seconds: Math.floor((Date.now() - testStartTime) / 1000)
    };
    
    if (existingIndex >= 0) {
      newAnswers[existingIndex] = answerData;
    } else {
      newAnswers.push(answerData);
    }
    
    setAnswers(newAnswers);
    
    // Move to next question
    if (currentQuestionIndex < activeTest.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleSubmitTest = async () => {
    if (!activeTest) return;
    
    setLoading(true);
    try {
      const totalTime = Math.floor((Date.now() - testStartTime) / 1000);
      
      const { data } = await managedAxios.post(`${API_BASE}/prelims-test-series/submit-test`, {
        test_id: activeTest.test_id,
        answers: answers,
        total_time_seconds: totalTime,
        pauses: 0
      });
      
      setTestResults(data);
      setShowResults(true);
      setTestInProgress(false);
      
      toast({ description: "Test submitted successfully!" });
    } catch (err) {
      toast({ 
        variant: "destructive", 
        description: err.response?.data?.detail || "Failed to submit test" 
      });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentAnswer = () => {
    return answers.find(a => a.question_index === currentQuestionIndex)?.selected_option;
  };

  // Loading state
  if (loading && !suiteData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Test in progress view
  if (testInProgress && activeTest) {
    const currentQuestion = activeTest.questions[currentQuestionIndex];
    const selectedAnswer = getCurrentAnswer();
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Test Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-slate-900">{activeTest.title}</h1>
              <p className="text-sm text-slate-500">{activeTest.ui_copy}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${timeRemaining < 300 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                <Clock className="w-5 h-5" />
                <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
              </div>
              <Badge variant="outline">
                {currentQuestionIndex + 1} / {activeTest.questions.length}
              </Badge>
            </div>
          </div>
          
          {/* Progress bar */}
          <Progress 
            value={(answers.length / activeTest.questions.length) * 100} 
            className="mt-4 h-2"
          />
        </div>

        {/* Question Card */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-6">
              <Badge className="shrink-0 bg-blue-100 text-blue-700">Q{currentQuestionIndex + 1}</Badge>
              {currentQuestion.difficulty && (
                <Badge variant="outline" className="shrink-0">
                  {currentQuestion.difficulty}
                </Badge>
              )}
              {currentQuestion.topic && (
                <Badge variant="outline" className="shrink-0">
                  {currentQuestion.topic}
                </Badge>
              )}
            </div>
            
            <p className="text-lg text-slate-900 mb-6 leading-relaxed">
              {currentQuestion.question}
            </p>

            {/* Options */}
            <div className="space-y-3">
              {Object.entries(currentQuestion.options || {}).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() => handleSelectAnswer(key)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                    selectedAnswer === key
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                      selectedAnswer === key
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-200 text-slate-700'
                    }`}>
                      {key}
                    </span>
                    <span className="text-slate-700">{value}</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1))}
            disabled={currentQuestionIndex === 0}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSkipQuestion}>
              Skip & Mark for Review
            </Button>
            
            {currentQuestionIndex === activeTest.questions.length - 1 ? (
              <Button 
                onClick={handleSubmitTest}
                className="bg-green-600 hover:bg-green-700"
                disabled={loading}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Test
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentQuestionIndex(currentQuestionIndex + 1)}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>

        {/* Question Navigator */}
        <Card className="mt-6">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Question Navigator</h3>
            <div className="flex flex-wrap gap-2">
              {activeTest.questions.map((_, idx) => {
                const answer = answers.find(a => a.question_index === idx);
                let bgColor = "bg-slate-200";
                if (idx === currentQuestionIndex) bgColor = "bg-blue-500 text-white";
                else if (answer?.selected_option) bgColor = "bg-green-500 text-white";
                else if (answer?.selected_option === null) bgColor = "bg-yellow-500 text-white";
                
                return (
                  <button
                    key={idx}
                    onClick={() => setCurrentQuestionIndex(idx)}
                    className={`w-8 h-8 rounded text-sm font-semibold ${bgColor}`}
                  >
                    {idx + 1}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-4 mt-3 text-xs text-slate-600">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-500" /> Answered
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-yellow-500" /> Skipped
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-slate-200" /> Not visited
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Results view
  if (showResults && testResults) {
    const { result, analysis } = testResults;
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button variant="outline" onClick={() => {
          setShowResults(false);
          setActiveTest(null);
          fetchSuiteOverview();
        }} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Test Series
        </Button>

        {/* Score Card */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white">
            <h1 className="text-2xl font-bold mb-2">Test Results</h1>
            <p className="text-blue-100">Your performance analysis</p>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-blue-600">{result.raw_score}</p>
                <p className="text-sm text-slate-600">Raw Score</p>
              </div>
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <p className="text-3xl font-bold text-green-600">{result.correct}</p>
                <p className="text-sm text-slate-600">Correct</p>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <p className="text-3xl font-bold text-red-600">{result.wrong}</p>
                <p className="text-sm text-slate-600">Wrong</p>
              </div>
              <div className="text-center p-4 bg-yellow-50 rounded-lg">
                <p className="text-3xl font-bold text-yellow-600">{result.skipped}</p>
                <p className="text-sm text-slate-600">Skipped</p>
              </div>
            </div>

            {/* FREE User Message */}
            {!isPro && analysis.message && (
              <Card className="bg-amber-50 border-amber-200 mb-6">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Lock className="w-5 h-5 text-amber-600" />
                    <p className="text-amber-900 font-medium">{analysis.message}</p>
                  </div>
                  <Button 
                    className="mt-4 bg-amber-500 hover:bg-amber-600"
                    onClick={() => navigate('/upgrade')}
                  >
                    Upgrade to PRO
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* PRO Analysis OR One-Time Free Analysis */}
            {(isPro || analysis.one_time_free_note) && analysis.behaviour_score !== undefined && (
              <>
                {/* One-Time Free Note */}
                {analysis.one_time_free_note && (
                  <Card className="mb-4 bg-amber-50 border-amber-200">
                    <CardContent className="p-4 flex items-center gap-3">
                      <Lock className="w-5 h-5 text-amber-600" />
                      <p className="text-amber-900">{analysis.one_time_free_note}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Behaviour Score with Components */}
                <Card className={`mb-4 ${
                  analysis.behaviour_label === 'Safe' ? 'border-green-300 bg-green-50' :
                  analysis.behaviour_label === 'Borderline' ? 'border-yellow-300 bg-yellow-50' :
                  'border-red-300 bg-red-50'
                }`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-slate-900">Prelims Behaviour Score</h3>
                        <p className="text-sm text-slate-600">Based on accuracy, attempt control, elimination, panic resistance</p>
                      </div>
                      <div className="text-center">
                        <p className="text-4xl font-bold">{analysis.behaviour_score}</p>
                        <Badge className={
                          analysis.behaviour_label === 'Safe' ? 'bg-green-500' :
                          analysis.behaviour_label === 'Borderline' ? 'bg-yellow-500' :
                          'bg-red-500'
                        }>
                          {analysis.behaviour_label}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Behaviour Components */}
                    {analysis.behaviour_components && (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 pt-4 border-t">
                        {Object.entries(analysis.behaviour_components).map(([key, value]) => (
                          <div key={key} className="text-center p-2 bg-white/50 rounded">
                            <p className="text-lg font-bold text-slate-800">{value}</p>
                            <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Why You Failed Engine */}
                {analysis.primary_failure_reason && (
                  <Card className="mb-4 border-red-200">
                    <CardContent className="p-4">
                      <h3 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5" />
                        Why You Lost Marks
                      </h3>
                      <div className="space-y-2">
                        <p className="text-slate-700">
                          <strong className="text-red-700">Primary:</strong> {analysis.primary_failure_reason}
                        </p>
                        {analysis.secondary_failure_reason && (
                          <p className="text-slate-600">
                            <strong className="text-orange-600">Secondary:</strong> {analysis.secondary_failure_reason}
                          </p>
                        )}
                        {analysis.one_behaviour_to_fix && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <p className="text-blue-900 font-medium">
                              <Target className="w-4 h-4 inline mr-2" />
                              Fix for Next Test: {analysis.one_behaviour_to_fix}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Time Leak Map */}
                {analysis.time_leaks?.length > 0 && (
                  <Card className="mb-4 border-purple-200">
                    <CardContent className="p-4">
                      <h3 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        Time Leak Map
                      </h3>
                      <div className="space-y-2">
                        {analysis.time_leaks.map((leak, idx) => (
                          <div key={idx} className={`flex items-center justify-between p-2 rounded ${
                            leak.verdict === 'wasted' ? 'bg-red-50' :
                            leak.verdict === 'efficient' ? 'bg-green-50' : 'bg-slate-50'
                          }`}>
                            <span className="font-medium text-slate-700">{leak.section}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-sm text-slate-500">{leak.time_spent_mins} mins</span>
                              <span className="text-sm font-medium">{leak.marks_gained} marks</span>
                              <Badge variant="outline" className={
                                leak.verdict === 'wasted' ? 'border-red-300 text-red-700' :
                                leak.verdict === 'efficient' ? 'border-green-300 text-green-700' : ''
                              }>
                                {leak.verdict}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Skip Intelligence */}
                {analysis.skip_intelligence && (
                  <Card className="mb-4 border-indigo-200">
                    <CardContent className="p-4">
                      <h3 className="font-bold text-indigo-900 mb-3 flex items-center gap-2">
                        <Brain className="w-5 h-5" />
                        Question Skip Intelligence
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium text-slate-600 mb-1">Ideal vs Actual Attempts</p>
                          <p className="text-lg font-bold">
                            {analysis.skip_intelligence.ideal_attempts || '-'} ideal / {analysis.skip_intelligence.actual_attempts} actual
                          </p>
                        </div>
                        {analysis.skip_intelligence.wrongly_attempted?.length > 0 && (
                          <div className="p-3 bg-red-50 rounded-lg">
                            <p className="text-sm font-medium text-red-700 mb-1">Should Have Skipped</p>
                            <p className="text-xs text-red-600">Q: {analysis.skip_intelligence.wrongly_attempted.join(', ')}</p>
                            {analysis.skip_intelligence.wrongly_attempted_reason && (
                              <p className="text-xs text-red-500 mt-1">{analysis.skip_intelligence.wrongly_attempted_reason}</p>
                            )}
                          </div>
                        )}
                        {analysis.skip_intelligence.good_skips?.length > 0 && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <p className="text-sm font-medium text-green-700 mb-1">Good Skip Decisions</p>
                            <p className="text-xs text-green-600">Q: {analysis.skip_intelligence.good_skips.join(', ')}</p>
                          </div>
                        )}
                        {analysis.skip_intelligence.should_have_attempted?.length > 0 && (
                          <div className="p-3 bg-yellow-50 rounded-lg">
                            <p className="text-sm font-medium text-yellow-700 mb-1">Missed Easy Questions</p>
                            <p className="text-xs text-yellow-600">Q: {analysis.skip_intelligence.should_have_attempted.join(', ')}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Cutoff Zone */}
                <Card className={`mb-4 ${
                  analysis.cutoff_zone === 'Safe' ? 'border-green-300' :
                  analysis.cutoff_zone === 'Borderline' ? 'border-yellow-300' :
                  'border-red-300'
                }`}>
                  <CardContent className="p-4">
                    <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Cutoff Safety Zone
                    </h3>
                    <Badge className={`text-lg px-4 py-1 ${
                      analysis.cutoff_zone === 'Safe' ? 'bg-green-500' :
                      analysis.cutoff_zone === 'Borderline' ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}>
                      {analysis.cutoff_zone}
                    </Badge>
                    {analysis.cutoff_zone_explanation && (
                      <p className="mt-2 text-sm text-slate-600">{analysis.cutoff_zone_explanation}</p>
                    )}
                    {analysis.zone_improvement_advice && (
                      <p className="mt-3 text-slate-700 p-3 bg-slate-50 rounded-lg">
                        <TrendingUp className="w-4 h-4 inline mr-2 text-blue-600" />
                        {analysis.zone_improvement_advice}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Stop Studying & Revision Priority */}
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  {/* Stop Studying List */}
                  {analysis.stop_studying_list?.length > 0 && (
                    <Card className="border-orange-200 bg-orange-50">
                      <CardContent className="p-4">
                        <h3 className="font-bold text-orange-900 mb-2 flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5" />
                          Stop Studying (Low Return)
                        </h3>
                        <ul className="space-y-1">
                          {analysis.stop_studying_list.map((topic, idx) => (
                            <li key={idx} className="text-slate-700 flex items-center gap-2 text-sm">
                              <span className="w-2 h-2 bg-orange-500 rounded-full" />
                              {topic}
                            </li>
                          ))}
                        </ul>
                        {analysis.stop_studying_reason && (
                          <p className="text-xs text-orange-600 mt-2">{analysis.stop_studying_reason}</p>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Revision Priority List */}
                  {analysis.revision_priority_list?.length > 0 && (
                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="p-4">
                        <h3 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                          <TrendingUp className="w-5 h-5" />
                          Revision Priority (High Return)
                        </h3>
                        <ul className="space-y-1">
                          {analysis.revision_priority_list.map((topic, idx) => (
                            <li key={idx} className="text-slate-700 flex items-center gap-2 text-sm">
                              <span className="w-2 h-2 bg-green-500 rounded-full" />
                              {topic}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Confidence Message */}
                {analysis.confidence_message && (
                  <Card className="bg-blue-50 border-blue-200">
                    <CardContent className="p-4">
                      <p className="text-blue-900 italic text-center">
                        &ldquo;{analysis.confidence_message}&rdquo;
                      </p>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Request One-Time Free Analysis Button (for FREE users who haven't used it) */}
            {!isPro && !analysis.behaviour_score && !analysis.one_time_free_note && (
              <div className="text-center mt-6">
                <p className="text-slate-600 mb-3">Want to see a sample of PRO analysis?</p>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const { data } = await managedAxios.post(`${API_BASE}/prelims-test-series/request-free-analysis/${result.test_id}`);
                      if (data.allowed) {
                        setTestResults(prev => ({
                          ...prev,
                          analysis: data.analysis
                        }));
                        toast({ description: "Full analysis unlocked! This is your one-time free preview." });
                      } else {
                        toast({ description: data.message, variant: "destructive" });
                      }
                    } catch (err) {
                      toast({ description: "Could not unlock free analysis", variant: "destructive" });
                    }
                  }}
                >
                  Unlock One-Time Free Analysis
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main suite overview
  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <Button variant="outline" onClick={() => navigate('/prelims')} className="mb-6">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Prelims Hub
      </Button>

      {/* Header - Core Positioning */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">UPSC Prelims Test Series</h1>
        <p className="text-slate-600 max-w-2xl mx-auto mb-2">
          We do not train aspirants to take mocks.<br/>
          <strong className="text-slate-800">We train aspirants to take the UPSC exam.</strong>
        </p>
        <p className="text-sm text-slate-500 italic max-w-xl mx-auto">
          &ldquo;Enough to Clear. Not Enough to Confuse.&rdquo;
        </p>
        {!isPro && (
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg">
            <Lock className="w-4 h-4" />
            <span className="text-sm font-medium">🔒 Free shows score. PRO shows whether you&apos;ll clear Prelims.</span>
          </div>
        )}
      </div>

      {/* Overall Progress */}
      {suiteData?.overall_progress && (
        <Card className="mb-8 bg-gradient-to-r from-slate-50 to-blue-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-slate-900">Your Progress</h2>
              <Badge variant="outline" className="text-lg">
                {suiteData.overall_progress.total_tests_completed} tests completed
              </Badge>
            </div>
            {isPro && suiteData.overall_progress.average_behaviour_score > 0 && (
              <div className="flex items-center gap-4">
                <span className="text-slate-600">Avg Behaviour Score:</span>
                <span className="text-2xl font-bold text-blue-600">
                  {Math.round(suiteData.overall_progress.average_behaviour_score)}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Test Suites Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {suiteData?.test_suites?.map((suite) => {
          const config = testTypeConfig[suite.type] || testTypeConfig.foundation;
          const Icon = config.icon;
          
          return (
            <Card 
              key={suite.type} 
              className={`overflow-hidden ${config.bgColor} ${config.borderColor} border-2 ${
                !suite.unlocked ? 'opacity-75' : ''
              }`}
            >
              <div className={`bg-gradient-to-r ${config.color} p-4 text-white`}>
                <div className="flex items-center justify-between">
                  <Icon className="w-8 h-8" />
                  {suite.is_pro_only && !isPro && (
                    <Badge className="bg-white/20">PRO</Badge>
                  )}
                </div>
                <h3 className="text-xl font-bold mt-2">{suite.name}</h3>
              </div>
              
              <CardContent className="p-4">
                <p className="text-sm text-slate-600 mb-3">{suite.description}</p>
                
                <div className="text-xs italic text-slate-500 mb-4 p-2 bg-white rounded">
                  &ldquo;{suite.ui_copy}&rdquo;
                </div>
                
                <div className="flex items-center justify-between text-sm text-slate-600 mb-4">
                  <span>{suite.questions_per_test} Qs</span>
                  <span>{suite.duration_minutes} mins</span>
                  <span>{suite.completed}/{suite.total_tests} done</span>
                </div>
                
                <Progress 
                  value={(suite.completed / suite.total_tests) * 100} 
                  className="mb-4 h-2"
                />
                
                {suite.unlocked ? (
                  <Button 
                    className={`w-full bg-gradient-to-r ${config.color}`}
                    onClick={() => handleStartTest(suite.type, suite.completed + 1)}
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                    Start Test {suite.completed + 1}
                  </Button>
                ) : (
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-slate-400 cursor-not-allowed"
                      disabled
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Locked
                    </Button>
                    <p className="text-xs text-center text-slate-500">{suite.unlock_reason}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* FREE User CTA */}
      {!isPro && (
        <Card className="mt-8 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-6 text-center">
            <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Unlock Full Behaviour Diagnostics</h2>
            <p className="text-slate-600 mb-4 max-w-lg mx-auto">
              Free shows score. <strong>PRO shows whether you&apos;ll clear Prelims.</strong>
              Get attempt strategy, skip intelligence, cutoff safety prediction, and stop-studying guidance.
            </p>
            <Button 
              className="bg-amber-500 hover:bg-amber-600"
              onClick={() => navigate('/upgrade')}
            >
              Upgrade to PRO
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PrelimsTestSeriesPageNew;
